import { useMemo } from "react";
import { Badge } from "@components/ui/badge";
import { format } from "date-fns";
import { formatDateToLocale } from "@utils/formatting";
import type { ApiTrainingSchedule } from "@shared-kernel/core/types/api-training-schedule";
import { formatHoursDisplay } from "@academy-context/primary/shared/trainings/training-schedule-form/shared/training-schedule-form.utils";
import { computeTotalScheduleHours } from "@shared-kernel/core/utils/training-schedule.utils";

// Extract date from an API schedule (uses morning.start or afternoon.start)
const extractDateFromSchedule = (schedule: ApiTrainingSchedule): Date => {
  const datetimeString = schedule.morning.enabled ? schedule.morning.start : schedule.afternoon.start;
  return new Date(datetimeString);
};

interface Props {
  schedules: ApiTrainingSchedule[] | null | undefined;
  /**
   * Custom title for the section
   * @default "Journées de formation"
   */
  title?: string;
  /**
   * Whether to show the section header
   * @default true
   */
  showHeader?: boolean;
  /**
   * Custom class name for the container
   */
  className?: string;
}

export const TrainingSchedulesDisplay = ({ schedules, title = "Journées de formation", showHeader = true, className }: Props) => {
  // Sort schedules by date
  const sortedSchedules = useMemo(() => {
    if (!schedules || schedules.length === 0) return [];
    return [...schedules].sort((a, b) => extractDateFromSchedule(a).getTime() - extractDateFromSchedule(b).getTime());
  }, [schedules]);

  const totalHours = useMemo(() => computeTotalScheduleHours(schedules ?? []), [schedules]);

  if (!schedules || schedules.length === 0) {
    return null;
  }

  const headerText = `${title} (total : ${formatHoursDisplay(totalHours)})`;

  return (
    <div className={className}>
      {showHeader && <h5 className="mb-2 flex items-center gap-2 text-sm font-medium">{headerText}</h5>}
      <div className="space-y-2 text-sm">
        <div className="flex items-center gap-3 text-xs text-muted-foreground">
          <span className="min-w-[100px]">Date</span>
          <div className="flex gap-2">
            <span className="w-[110px]">Matin</span>
            <span>Après-midi</span>
          </div>
        </div>
        {sortedSchedules.map((schedule, idx) => (
          <div key={idx} className="flex items-center gap-3">
            <span className="min-w-[100px] font-medium">{formatDateToLocale(extractDateFromSchedule(schedule).toISOString())}</span>
            <div className="flex gap-2">
              <div className="w-[110px]">
                {schedule.morning.enabled && (
                  <Badge variant="outline" className="text-xs">
                    {format(new Date(schedule.morning.start), "HH:mm")} - {format(new Date(schedule.morning.end), "HH:mm")}
                  </Badge>
                )}
              </div>
              {schedule.afternoon.enabled && (
                <Badge variant="outline" className="text-xs">
                  {format(new Date(schedule.afternoon.start), "HH:mm")} - {format(new Date(schedule.afternoon.end), "HH:mm")}
                </Badge>
              )}
            </div>
          </div>
        ))}
      </div>
    </div>
  );
};
